import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ActivatedRouteSnapshot, RouteReuseStrategy } from '@angular/router';
import { JWT_OPTIONS, JwtModule, JwtModuleOptions } from '@auth0/angular-jwt';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { CookieService } from 'ngx-cookie-service';
import { ToastrModule } from 'ngx-toastr';

import {
  ChannelStubService,
  ChannelService,
  StorageService,
  tokenGetter,
  USER_ACTIVITY_CONFIG,
  AppLoadService,
  initialize,
  LocaleIdService,
  PLATFORM_LOGGER,
  SPINNER_CONTROL,
  FEATURE_FLAG_READER,
  CURRENT_USER_READER,
  COMPANY_READER,
  NOTIFICATIONS_READER,
  REAL_TIME_CONTROL,
  CURRENT_PERSON_READER,
  CURRENT_PERSON_CONTROL,
  BILLING_STATUS_READER,
  TEAM_LIST_READER,
  TEAM_LIST_CONTROL,
  COMPANY_USERS_READER,
  LANGUAGE_READER,
  AUTH_CONTROL,
} from '@ninety/platform/services';
import { DatadogRumUserSyncService } from '@ninety/platform/services/services/datadog-rum-sync.service';
import { DatadogLogger } from '@ninety/platform/services/vendor/datadog';
import { DatadogLoggerModule } from '@ninety/platform/services/vendor/datadog/datadog-logger.module';
import { DatadogRumModule } from '@ninety/platform/services/vendor/datadog/datadog-rum.module';
import {
  FeatureFlagFacade,
  NOTIFY_CONTROL,
  PlatformServicesStateDispatcher,
  PlatformServicesStateReader,
  SpinnerService,
  USER_SERVICE,
} from '@ninety/platform/state';
import { AppEntitiesStateModule } from '@ninety/platform/state/app-entities/app-entities-state.module';
import { AppGlobalStateModule } from '@ninety/platform/state/app-global/app-global-state.module';
import { globalReducers, globalStateEffects } from '@ninety/platform/state/state-setup';
import { BrandingThemeDirective } from '@ninety/ui/core';
import { FROALA_LICENSE_KEY } from '@ninety/ui/legacy/components/ninety-rich-text-editor';
import { GoogleAnalyticsModule } from '@ninety/ui/legacy/core/analytics/google-analytics/google-analytics.module';
import { NinetyAuthServiceInterceptor } from '@ninety/ui/legacy/core/interceptors/auth.interceptor';
import { BillingInterceptor } from '@ninety/ui/legacy/core/interceptors/billing.interceptor';
import { EntitlementsInterceptor } from '@ninety/ui/legacy/core/interceptors/entitlements.interceptor';
import { HostInterceptor } from '@ninety/ui/legacy/core/interceptors/host.interceptor';
import { ErrorInterceptor } from '@ninety/ui/legacy/core/interceptors/http-error.interceptor';
import { NotificationsInterceptor } from '@ninety/ui/legacy/core/interceptors/notifications.interceptor';
import { OriginInterceptor } from '@ninety/ui/legacy/core/interceptors/origin.interceptor';
import { RefreshTokenInterceptor } from '@ninety/ui/legacy/core/interceptors/refresh-token.interceptor';
import { RequestIdInterceptor } from '@ninety/ui/legacy/core/interceptors/request-id.interceptor';
import { NotifyService } from '@ninety/ui/legacy/core/services/notify.service';
import { UrlRouteReuseStrategy } from '@ninety/ui/legacy/core/services/url-route-reuse.strategy';
import { UserService } from '@ninety/ui/legacy/core/services/user.service';
import { OpenTelemetryModule } from '@ninety/ui/legacy/core/vendor/opentelemetry/opentelemetry.module';
import { provideDefaultTippyConfig } from '@ninety/ui/legacy/core/vendor/tippy.config';
import { SharedModule } from '@ninety/ui/legacy/shared/shared.module';
import { ENVIRONMENT, environment } from '@ninety/ui/web/environments';
import { OneSchemaDataImportModule } from '@ninety/web/pages/data-import/data-import.module';
import { UserDetailStateKey } from '@ninety/web/pages/directory/_state/user-detail-state.model';
import { UserDetailEffects } from '@ninety/web/pages/directory/_state/user-detail.effects';
import { UserDetailReducer } from '@ninety/web/pages/directory/_state/user-detail.reducer';
import { provideAgChartsTheme } from '@ninety/web/pages/insights/disco-visualizations/config/themes';
import { UniversalCreateService } from '@ninety/web/pages/layouts/services/universal-create.service';
import { LoginSharedStateFacade } from '@ninety/web/pages/login/_state/shared/login-shared-state.facade';
import { UNIVERSAL_CREATE_ITEM_CREATOR } from '@ninety/web/pages/universal-create';

import { ADD_TEAMMATES_DIALOG_PROVIDERS } from './add-teammates-dialog.providers';
import { ALBATO_MONDAY_UPDATE_BANNER_PROVIDERS } from './albato-monday-update-banner.providers';
import { AppFeatureStateModule } from './app-feature-state.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ATTACHMENTS_PROVIDERS } from './attachments.providers';
import { AUTH_DIALOG_PROVIDERS } from './auth-dialog.providers';
import { BILLING_DIALOG_PROVIDERS } from './billing-dialog.providers';
import { CONTACT_CARD_PROVIDERS } from './contact-card.providers';
import { CUSTOM_LANGUAGE_PROVIDERS } from './custom-language.providers';
import { FOLLOWERS_PROVIDERS } from './followers.providers';
import { INLINE_EDITABLE_DUE_DATE_PROVIDERS } from './inline-editable-due-date.providers';
import { INLINE_EDITABLE_TITLE_PROVIDERS } from './inline-editable-title.providers';
import { INLINE_ROLE_SELECT_PROVIDERS } from './inline-role-select.providers';
import { NOTIFICATION_BANNER_PROVIDERS } from './notification-banner.providers';
import { NOTIFY_PROVIDERS } from './notify.providers';
import { ROCKS_QUARTER_MENU_PROVIDERS } from './rocks-quarter-menu.providers';
import { TEAM_CARD_PROVIDERS } from './team-card.providers';
import { TRASH_TABLE_PROVIDERS } from './trash-table.providers';
import { TREE_PROVIDERS } from './tree.providers';
import { UPLOAD_DIALOG_PROVIDERS } from './upload-dialog.providers';
import { USER_SELECT_PROVIDERS } from './user-select.providers';
import { USERS_DIALOG_PROVIDERS } from './users-dialog.providers';
import { VIDYARD_PROVIDERS } from './vidyard.providers';
/* DO NOT import Lazy Loaded Modules...It will cause a run time error */

export function jwtOptionsFactory() {
  const options: JwtModuleOptions = {
    config: {
      tokenGetter: tokenGetter,
      allowedDomains: [/^.*?app\.ninety\.io$/, /^localhost:(3001|3002|3080|4200|8080)$/],
      disallowedRoutes: [/\/v4\/assets\/*/],
    },
  };
  return options.config;
}

export const TooltipOptions: MatTooltipDefaultOptions = {
  showDelay: 500,
  hideDelay: 0,
  touchendHideDelay: 0,
  disableTooltipInteractivity: true,
};

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    DatadogLoggerModule.forRoot(),
    DatadogRumModule.forRoot(),
    BrowserModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
      countDuplicates: true,
      closeButton: true,
      resetTimeoutOnDuplicate: true,
    }),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
      },
    }),
    // Feature Modules
    SharedModule,
    // AppRouting module must be declared last in order to prevent the ** from catching
    AppRoutingModule,
    StoreModule.forRoot({
      ...globalReducers,
      [UserDetailStateKey]: UserDetailReducer,
    }),
    EffectsModule.forRoot([...globalStateEffects, UserDetailEffects]),
    StoreRouterConnectingModule.forRoot(),
    !environment.production ? StoreDevtoolsModule.instrument({ name: 'Ninety', maxAge: 50, connectInZone: true }) : [],
    AppGlobalStateModule,
    AppEntitiesStateModule,
    AppFeatureStateModule,
    GoogleAnalyticsModule,
    BrandingThemeDirective,
    OpenTelemetryModule,
    OneSchemaDataImportModule,
    RecaptchaV3Module,
  ],
  providers: [
    { provide: ENVIRONMENT, useValue: environment },
    { provide: FROALA_LICENSE_KEY, useValue: environment.froalaApiKey },
    // Binds the platform logger abstraction to the real datadog logger. Lets platform/state
    // services (e.g. EntitlementsService) log without depending on shared/ui/core. (DEV-26105)
    { provide: PLATFORM_LOGGER, useExisting: DatadogLogger },
    // Binds the spinner-control abstraction to the real SpinnerService (now in platform/state).
    // Lets services that stay in platform/services drive the spinner without re-forming the
    // platform/services -> platform/state cycle. (DEV-26105)
    { provide: SPINNER_CONTROL, useExisting: SpinnerService },
    // Binds the feature-flag-reader abstraction to the real FeatureFlagFacade (now in platform/state).
    // Lets AuthService/LaunchDarklyService (in platform/services) read flags + push LaunchDarkly
    // updates without re-forming the platform/services -> platform/state cycle. (DEV-26105 PR2)
    { provide: FEATURE_FLAG_READER, useExisting: FeatureFlagFacade },
    // Binds the read-only reader abstractions to the real platform/state reader. Lets services that
    // stay in platform/services (DataLayerService, DatadogRumUserSyncService, OneSignalService, …) read
    // current user / company / notifications state without re-forming the platform/services ->
    // platform/state cycle. (DEV-26105 PR4)
    { provide: CURRENT_USER_READER, useExisting: PlatformServicesStateReader },
    { provide: COMPANY_READER, useExisting: PlatformServicesStateReader },
    { provide: NOTIFICATIONS_READER, useExisting: PlatformServicesStateReader },
    { provide: CURRENT_PERSON_READER, useExisting: PlatformServicesStateReader },
    { provide: BILLING_STATUS_READER, useExisting: PlatformServicesStateReader },
    { provide: TEAM_LIST_READER, useExisting: PlatformServicesStateReader },
    { provide: COMPANY_USERS_READER, useExisting: PlatformServicesStateReader },
    { provide: LANGUAGE_READER, useExisting: PlatformServicesStateReader },
    // Command ("control") tokens — bound to the platform/state dispatcher so staying services
    // (e.g. ChannelService, PersonService) dispatch actions without importing platform/state. (DEV-26105 PR4)
    { provide: REAL_TIME_CONTROL, useExisting: PlatformServicesStateDispatcher },
    { provide: CURRENT_PERSON_CONTROL, useExisting: PlatformServicesStateDispatcher },
    { provide: TEAM_LIST_CONTROL, useExisting: PlatformServicesStateDispatcher },
    { provide: AUTH_CONTROL, useExisting: PlatformServicesStateDispatcher },
    // Binds the notify/user-service abstractions to the real shared/ui/core services. Lets the
    // notification/team-list/user-list effects (now in platform/state) drive notifications and
    // user fetches without re-forming the platform/state -> shared/ui/core edge. (DEV-26105 T5)
    { provide: NOTIFY_CONTROL, useExisting: NotifyService },
    { provide: USER_SERVICE, useExisting: UserService },
    // Binds the universal-create item creator to the real layouts UniversalCreateService.
    // Lets the universal-create effects (root-level singletons) call the create logic without
    // depending on the layouts lib directly (which would form a circular dep). (DEV-26457)
    { provide: UNIVERSAL_CREATE_ITEM_CREATOR, useExisting: UniversalCreateService },
    // Binds the auth dialog-opener tokens + span-control to the real shared/ui/core dialog components
    // and SpanService. Lets the auth dialog-opening effects (now in platform/state) open dialogs and
    // trace without re-forming the platform/state -> shared/ui/core edge. (DEV-26105 T6a)
    ...ADD_TEAMMATES_DIALOG_PROVIDERS,
    ...ALBATO_MONDAY_UPDATE_BANNER_PROVIDERS,
    ...ATTACHMENTS_PROVIDERS,
    ...AUTH_DIALOG_PROVIDERS,
    ...BILLING_DIALOG_PROVIDERS,
    ...CONTACT_CARD_PROVIDERS,
    ...CUSTOM_LANGUAGE_PROVIDERS,
    ...USERS_DIALOG_PROVIDERS,
    ...FOLLOWERS_PROVIDERS,
    ...INLINE_EDITABLE_DUE_DATE_PROVIDERS,
    ...INLINE_EDITABLE_TITLE_PROVIDERS,
    ...INLINE_ROLE_SELECT_PROVIDERS,
    ...NOTIFICATION_BANNER_PROVIDERS,
    ...NOTIFY_PROVIDERS,
    ...ROCKS_QUARTER_MENU_PROVIDERS,
    ...TEAM_CARD_PROVIDERS,
    ...UPLOAD_DIALOG_PROVIDERS,
    ...TRASH_TABLE_PROVIDERS,
    ...TREE_PROVIDERS,
    ...USER_SELECT_PROVIDERS,
    ...VIDYARD_PROVIDERS,
    AppLoadService,
    CookieService,
    provideAppInitializer(() => {
      const initializerFn = initialize(inject(AppLoadService));
      return initializerFn();
    }),
    { provide: StorageService, useFactory: () => new StorageService(localStorage) },

    {
      provide: 'oldVersionUrlRedirectResolver',
      useValue: (route: ActivatedRouteSnapshot) => {
        window.location.href = window.location.origin + route.data.oldVersionUrl;
      },
    },
    {
      provide: LOCALE_ID,
      useClass: LocaleIdService,
    },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: TooltipOptions },
    DatadogRumUserSyncService,
    DatePipe,
    LoginSharedStateFacade,
    provideDefaultTippyConfig(),
    provideAgChartsTheme(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { provide: ChannelService, useClass: (window as any).Cypress ? ChannelStubService : ChannelService },
    {
      provide: USER_ACTIVITY_CONFIG,
      useValue: {
        inactivityTimeoutSeconds: 15 * 60, // 15 minutes
        reloadThresholdSeconds: 4 * 60 * 60, // 4 hours
      },
    },
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([
        HostInterceptor,
        RequestIdInterceptor,
        ErrorInterceptor,
        RefreshTokenInterceptor,
        OriginInterceptor,
        EntitlementsInterceptor,
        BillingInterceptor,
        NinetyAuthServiceInterceptor,
        NotificationsInterceptor,
      ])
    ),
    provideNativeDateAdapter(),
    // Custom `RouteReuseStrategy` to allow components to detach and re-attach during navigation based on Route configuration.
    { provide: RouteReuseStrategy, useClass: UrlRouteReuseStrategy },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: {
        usePopover: false,
      },
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.NINETY_GOOGLE_RECAPTCHA_SITEKEY,
    },
  ],
})
export class AppModule {
  constructor(private readonly store: Store) {
    /** Make the store available to cypress tests specifically for updating feature flags */
    if (!environment.production) {
      (window as Window & { ninety?: { store: Store } }).ninety = { store: this.store };
    }
  }
}
